import { isAddonLine } from './orderAddons';
import { buildOrderItems } from './storefrontOrder/payload';

const norm = value => String(value ?? '').trim().toLowerCase();
const isYes = value => ['1', 'enabled', 'on', 'true', 'yes'].includes(norm(value));

const prop = (line, names) => {
  const wanted = names.map(norm);
  return (Array.isArray(line?.properties) ? line.properties : [])
    .find(p => wanted.includes(norm(p?.name)))?.value;
};

const PREMIUM_PROFILE = 'premium_hardcover_v1';

const addonKind = line => {
  if (line?.addon_type === 'gift_box') return 'giftBox';
  if (line?.addon_type === 'gift_note') return 'giftNote';
  const keys = [line?.raw_sku, line?.sku].map(norm).filter(Boolean);
  if (keys.some(k => ['gift', 'gift-box'].includes(k))) return 'giftBox';
  if (keys.some(k => ['gift-note', 'giftnote'].includes(k))) return 'giftNote';
  const title = norm(line?.title || line?.name);
  if (title === 'gift box' || title.includes('gift wrapping')) return 'giftBox';
  if (title.includes('gift note')) return 'giftNote';
  return null;
};

const findVariant = (catalog, variantId) => {
  if (variantId === undefined || variantId === null) return null;
  const id = String(variantId);
  for (const story of catalog || []) {
    for (const variant of story.variants || []) {
      if (String(variant.id) === id) return { catalogStory: story, variant, viaPremium: false };
      if (variant.premium && String(variant.premium.variant_id) === id) {
        return { catalogStory: story, variant, viaPremium: true };
      }
    }
  }
  return null;
};

const exactProp = (line, name) => (
  (line?.properties || []).find(p => p && p.name === name)?.value
);

/*
 * Draft = {
 *   stories: DraftStory[],
 *   discount: { amount: string, reason: string },
 *   discountOriginal: { amount: string, reason: string }  // order's discount at load, for change counting
 * }
 * DraftStory.addonsAmbiguous is true when several same-named story lines share
 * linked add-ons; the backend rejects add-on edits for them, so they are never sent.
 */
export function buildDraft(order, catalog) {
  const lines = Array.isArray(order?.line_items) ? order.line_items : [];
  const addonLines = lines.filter(isAddonLine);
  const storyLines = lines.filter(line => !isAddonLine(line));
  const stories = storyLines.map((line, index) => {
    const match = findVariant(catalog, line.variant_id);
    const linked = line.name
      ? addonLines.filter(a => exactProp(a, '_Related Story') === line.name)
      : [];
    const kinds = linked.map(addonKind);
    const addons = {
      giftBox: kinds.includes('giftBox'),
      giftNote: kinds.includes('giftNote'),
      giftMessage: String(exactProp(line, 'Gift Message') ?? ''),
    };
    const premiumOriginal = match
      ? match.viaPremium
      : exactProp(line, '_PrintingProfile') === PREMIUM_PROFILE;
    const addonsAmbiguous = linked.length > 0
      && storyLines.some(l => l !== line && l.name === line.name);
    return {
      key: `line-${line.id ?? index}`,
      lineItemId: line.id !== undefined && line.id !== null ? String(line.id) : null,
      line,
      catalogStory: match?.catalogStory || null,
      variant: match?.variant || null,
      premium: premiumOriginal,
      premiumOriginal,
      addons,
      addonsOriginal: { ...addons },
      addonsAmbiguous,
      removed: false,
      replacement: null,
      newEntry: null,
    };
  });
  const sd = order?.special_discount;
  const discount = {
    amount: sd && Number(sd.amount) ? String(sd.amount) : '',
    reason: sd?.reason || '',
  };
  const originalShipping = order?.shipping_lines?.[0]?.code || 'regular_shipping';
  const shipping = { code: originalShipping, originalCode: originalShipping };
  return { stories, discount, discountOriginal: { ...discount }, shipping };
}

export function getPrintingControl(story) {
  if (!story?.catalogStory || !story.variant) {
    return { visible: true, editable: false, note: 'Not in the catalog' };
  }
  if (story.catalogStory.story_type === 'legacy_static') {
    return { visible: true, editable: false, note: 'Premium only' };
  }
  if (!story.variant.premium) return { visible: false, editable: false };
  if (story.line?.price_override !== undefined && story.line?.price_override !== null) {
    return { visible: true, editable: false, note: 'Price set by override' };
  }
  return { visible: true, editable: true };
}

const shippingChanged = draft => !!draft?.shipping
  && draft.shipping.code !== draft.shipping.originalCode;

const discountChanged = draft => {
  const a = draft.discount || { amount: '', reason: '' };
  const b = draft.discountOriginal || { amount: '', reason: '' };
  return (Number(a.amount) || 0) !== (Number(b.amount) || 0)
    || String(a.reason || '').trim() !== String(b.reason || '').trim();
};

const msgChanged = (s) => (
  s.addons.giftMessage.trim() !== s.addonsOriginal.giftMessage.trim()
);

const addonFlagsChanged = s => (
  s.addons.giftBox !== s.addonsOriginal.giftBox
  || s.addons.giftNote !== s.addonsOriginal.giftNote
);

export function countChanges(draft) {
  let count = 0;
  (draft?.stories || []).forEach(s => {
    if (s.newEntry) {
      if (!s.removed) count += 1;
      return;
    }
    if (s.removed || s.replacement) { count += 1; return; }
    if (s.premium !== s.premiumOriginal) count += 1;
    if (s.addonsAmbiguous) return;
    if (s.addons.giftBox !== s.addonsOriginal.giftBox) count += 1;
    if (s.addons.giftNote !== s.addonsOriginal.giftNote) count += 1;
    if (s.addons.giftNote && s.addonsOriginal.giftNote && msgChanged(s)) count += 1;
  });
  if (draft && discountChanged(draft)) count += 1;
  if (shippingChanged(draft)) count += 1;
  return count;
}

export const isDirty = draft => countChanges(draft) > 0;

const addonsEdited = s => !s.addonsAmbiguous && (
  addonFlagsChanged(s) || (s.addons.giftNote && msgChanged(s))
);

export function toDesiredState(draft, editRequestId) {
  const stories = [];
  (draft?.stories || []).forEach(s => {
    if (s.newEntry) {
      if (!s.removed) stories.push({ line_item_id: null, items: buildOrderItems([s.newEntry]) });
      return;
    }
    if (s.removed) {
      stories.push({ line_item_id: s.lineItemId, remove: true });
      return;
    }
    if (s.replacement) {
      stories.push({
        line_item_id: s.lineItemId,
        replace: true,
        items: buildOrderItems([s.replacement]),
      });
      return;
    }
    const out = { line_item_id: s.lineItemId };
    if (s.premium !== s.premiumOriginal) out.premium = s.premium;
    if (addonsEdited(s)) {
      out.addons = {
        gift_box: s.addons.giftBox,
        gift_note: s.addons.giftNote,
        gift_message: s.addons.giftNote ? s.addons.giftMessage : '',
      };
    }
    stories.push(out);
  });
  const amount = Number(draft?.discount?.amount);
  const discount = amount > 0 || amount < 0 || Number.isNaN(amount)
    ? { amount, reason: String(draft.discount.reason || '').trim() }
    : null;
  const body = { stories, discount };
  if (shippingChanged(draft)) body.shipping = { code: draft.shipping.code };
  if (editRequestId) body.edit_request_id = editRequestId;
  return body;
}

// Desired state that changes nothing but the discount. An empty catalog is enough
// because no story is replaced or switched, so tier lookups are never needed.
export function buildDiscountOnlyState(order, discount, editRequestId) {
  const draft = buildDraft(order, []);
  return toDesiredState({ ...draft, discount }, editRequestId);
}

const remainingStories = (draft, exceptKey) => (draft?.stories || [])
  .filter(s => s.key !== exceptKey && !s.removed).length;

export const canRemoveStory = (draft, key) => remainingStories(draft, key) > 0;

export function getDraftErrors(draft) {
  const errors = [];
  const add = message => { if (!errors.includes(message)) errors.push(message); };
  if (remainingStories(draft) === 0) add('An order must keep at least one story');
  (draft?.stories || []).forEach(s => {
    if (s.newEntry || s.removed || s.replacement || s.addonsAmbiguous || !s.addons.giftNote) return;
    const newlyOn = !s.addonsOriginal.giftNote;
    if (!newlyOn && !msgChanged(s)) return;
    const length = s.addons.giftMessage.trim().length;
    if (length < 2 || length > 250) add('Gift message must be between 2 and 250 characters.');
  });
  const rawAmount = String(draft?.discount?.amount ?? '').trim();
  if (rawAmount !== '') {
    const amount = Number(rawAmount);
    if (!Number.isFinite(amount) || amount < 0
      || Math.abs(amount * 100 - Math.round(amount * 100)) > 1e-6) {
      add('Enter a valid discount amount');
    } else if (amount > 0 && String(draft.discount.reason || '').trim().length < 4) {
      add('Discount reason must be at least 4 characters');
    }
  }
  return errors;
}
